import React, { useEffect, useRef, useState } from "react";

import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  InputAdornment,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import adminAxios from "../../adminApi/adminApi";
import { useAdminAuth } from "../../hooks/AdminAuthProvider";
import { GET_ALL_ACTIVE_USER_SESSIONS_PER_TENANT_VIEW } from "../../config/urls";
import CustomPagination from "../../common/custom-pagination";

const STATUS_COLORS = { active: "success", idle: "warning", completed: "default" };

// User Sessions V2, filtered and paged by the backend
const ActiveUserSessionsTable = ({ tenants, capitalize, convertTimestamp }) => {
  const { isAdminTokenValid, adminLogout } = useAdminAuth();
  const [sessions, setSessions] = useState([]);
  const [count, setCount] = useState(0);
  const [statuses, setStatuses] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedTenant, setSelectedTenant] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Only the latest request may update the table, so a slow older one can't show stale rows
  const latestRequest = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!isAdminTokenValid()) {
      adminLogout();
      return;
    }
    fetchSessions();
  }, [debouncedSearch, selectedTenant, statusFilter, page, rowsPerPage]);

  const fetchSessions = async () => {
    const requestId = ++latestRequest.current;
    try {
      setLoading(true);
      const response = await adminAxios.get(
        GET_ALL_ACTIVE_USER_SESSIONS_PER_TENANT_VIEW,
        {
          params: {
            page,
            page_size: rowsPerPage,
            search: debouncedSearch || undefined,
            tenant_id: selectedTenant === "all" ? undefined : selectedTenant,
            status: statusFilter === "all" ? undefined : statusFilter,
          },
        }
      );
      if (requestId !== latestRequest.current) return;
      setSessions(response.data.user_sessions_data);
      setCount(response.data.count);
      setStatuses(response.data.statuses);
      setError(null);
    } catch (err) {
      if (
        err.message === "Token expired" ||
        err.message === "No access token"
      ) {
        // Already handled by interceptor
        return;
      }
      if (requestId !== latestRequest.current) return;
      setError(err.response?.data?.detail || "Failed to fetch sessions");
      console.error("Error fetching sessions:", err);
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  };

  const changeFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
  };

  return (
    <Paper>
      <Box
        sx={{
          p: 2,
          bgcolor: "grey.50",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          User Sessions Per Tenant
        </Typography>
      </Box>
      <Box
        sx={{
          p: 2,
          bgcolor: "grey.50",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search tenant or phone number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Tenant Filter</InputLabel>
              <Select
                value={selectedTenant}
                label="Tenant Filter"
                onChange={changeFilter(setSelectedTenant)}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">All Tenants</MenuItem>
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={changeFilter(setStatusFilter)}
              >
                <MenuItem value="all">All Status</MenuItem>
                {statuses.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {loading ? "Loading sessions..." : `${count} sessions`}
        </Typography>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Tenant Name</strong>
              </TableCell>
              <TableCell>
                <strong>Phone Number</strong>
              </TableCell>
              <TableCell>
                <strong>Status</strong>
              </TableCell>
              <TableCell>
                <strong>Created Date</strong>
              </TableCell>
              <TableCell>
                <strong>Expiry Date</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map((session) => (
              <TableRow key={session.user_session_id} hover>
                <TableCell>{session.tenant_name}</TableCell>
                <TableCell>{session.phone_number}</TableCell>
                <TableCell>
                  <Chip
                    label={capitalize(session.status)}
                    size="small"
                    color={STATUS_COLORS[session.status] || "default"}
                  />
                </TableCell>
                <TableCell>{convertTimestamp(session.started_on)}</TableCell>
                <TableCell>
                  {convertTimestamp(session.expiry_date) || "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {count > 0 && (
        <Box sx={{ borderTop: 1, borderColor: "divider" }}>
          <CustomPagination
            count={count}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={changeFilter(setRowsPerPage)}
          />
        </Box>
      )}
      {!loading && (error || count === 0) && (
        <Box sx={{ p: 8, textAlign: "center" }}>
          <ErrorIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {error || "No sessions found"}
          </Typography>
          {!error && (
            <Typography variant="body2" color="text.secondary">
              Try adjusting your filters
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default ActiveUserSessionsTable;
